import * as React from "react";
import { HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { AppColorVariant } from "@/theme/AppColorVariant";
import {
  ConflictReason,
  ConflictReasonColors,
  ConflictReasonDescriptions,
  ConflictReasonIcons,
  ConflictReasonLabels,
} from "@/models/ConflictReason";
import {
  ConflictResolutionAction,
  ConflictResolutionActionColors,
  ConflictResolutionActionDescriptions,
  ConflictResolutionActionIcons,
  ConflictResolutionActionLabels,
} from "@/models/ConflictResolutionAction";
import {
  MigrationFlagStatus,
  ConflictStatusColors,
  ConflictStatusDescriptions,
  ConflictStatusLabels,
} from "@/models/Flags/MigrationFlagStatus";

interface LegendItem {
  key: string;
  label: string;
  color: AppColorVariant;
  description: string;
  Icon?: React.ElementType;
}

const REASON_ITEMS: LegendItem[] = Object.values(ConflictReason).map((r) => ({
  key: r,
  label: ConflictReasonLabels[r],
  color: ConflictReasonColors[r],
  description: ConflictReasonDescriptions[r],
  Icon: ConflictReasonIcons[r],
}));

const ACTION_ITEMS: LegendItem[] = Object.values(ConflictResolutionAction).map((a) => ({
  key: a,
  label: ConflictResolutionActionLabels[a],
  color: ConflictResolutionActionColors[a],
  description: ConflictResolutionActionDescriptions[a],
  Icon: ConflictResolutionActionIcons[a],
}));

const STATUS_ITEMS: LegendItem[] = Object.values(MigrationFlagStatus).map((s) => ({
  key: s,
  label: ConflictStatusLabels[s],
  color: ConflictStatusColors[s],
  description: ConflictStatusDescriptions[s],
}));

const LegendSection: React.FC<{ title: string; items: LegendItem[] }> = ({
  title,
  items,
}) => (
  <div className="flex flex-col gap-2">
    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {title}
    </p>
    <ul className="flex flex-col gap-2">
      {items.map((item) => (
        <li key={item.key} className="flex flex-col gap-0.5">
          <Badge variant={item.color} className="w-fit gap-1">
            {item.Icon && <item.Icon className="size-3" />}
            {item.label}
          </Badge>
          <p className="text-xs text-muted-foreground">{item.description}</p>
        </li>
      ))}
    </ul>
  </div>
);

/**
 * Help popover explaining the conflict vocabulary — reasons, resolution actions, and statuses. Built entirely
 * from the shared enum metadata so it never drifts from what the resolve drawer actually shows.
 */
export const ConflictLegend: React.FC = () => (
  <Popover>
    <PopoverTrigger asChild>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="text-muted-foreground"
        aria-label="What do these conflict terms mean?"
      >
        <HelpCircle className="size-4" />
      </Button>
    </PopoverTrigger>
    <PopoverContent
      align="end"
      className="flex max-h-[70vh] w-96 flex-col gap-4 overflow-y-auto"
    >
      <LegendSection title="Conflict reasons" items={REASON_ITEMS} />
      <LegendSection title="Resolution actions" items={ACTION_ITEMS} />
      <LegendSection title="Status" items={STATUS_ITEMS} />
    </PopoverContent>
  </Popover>
);

export default ConflictLegend;
